import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import type { FeedCollection } from "@/api";
import { DownloadIcon, EyeOffIcon, FavoriteHeartIcon, ShareIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ideaLikeId } from "@/store";
import { useIdeaHidden, useSetIdeaHidden } from "@/store/hidden-ideas";
import { downloadIdeaImage } from "./download-idea-image";
import { useIdeaSaved } from "./use-idea-saved";

// Ряд действий с идеей под описанием (кадры «Вертикальная идея 2» 3896:113819 и
// «Горизонтальная идея» 3535:46699, замечание Айталы A-25): «В избранное»
// (сердце 30×30 с обводкой 1,5 + подпись 14/18 w600, зазор 6), затем три
// круглые кнопки 40×40 без заливки с иконками 24: скачать, поделиться, скрыть.
// Между элементами 10. Иконки и круглые кнопки — из DS (FavoriteHeartIcon
// «на странице», DownloadIcon / ShareIcon / EyeOffIcon, Button icon-md ghost),
// цвет знаков #333333 как в кадре.
const ACTION = "text-ds-ink-3 disabled:bg-transparent disabled:text-ds-ink-3/50";
export function IdeaActions({
  collection: c,
  imageUrl,
  imageIndex
}: {
  collection: FeedCollection;
  imageUrl: string;
  imageIndex: number;
}) {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    saved,
    toggle
  } = useIdeaSaved(ideaLikeId(c), c.id);
  const hidden = useIdeaHidden(c);
  const setHidden = useSetIdeaHidden();
  const [downloading, setDownloading] = useState(false);
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({
        title: c.title,
        text: c.description,
        url
      });else {
        await navigator.clipboard.writeText(url);
        toast.success(t("feed.shareCopied", {
          defaultValue: "Ссылка скопирована"
        }));
      }
    } catch {
      /* покупатель закрыл окно «Поделиться» */
    }
  };
  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const many = (c.images.length || 1) > 1;
      await downloadIdeaImage(imageUrl, `osmix-${c.slug}${many ? `-${imageIndex + 1}` : ""}`);
    } catch {
      toast.error(t("feed.downloadFailed", {
        defaultValue: "Не удалось скачать фото. Попробуйте ещё раз"
      }));
    } finally {
      setDownloading(false);
    }
  };

  // A-26: «Скрыть» — назад, туда, откуда пришли (на стене идея видна скрытой).
  // Уже скрытую идею (открыли по ссылке) та же кнопка возвращает, без перехода.
  const toggleHidden = () => {
    const next = !hidden;
    setHidden(c, next).catch(() => toast.error(t("feed.hideFailed", {
      defaultValue: "Не удалось сохранить. Попробуйте ещё раз"
    })));
    if (!next) return;
    if (location.key !== "default") navigate(-1);else navigate("/ideas");
  };
  return <div className="flex h-10 items-center gap-2.5">
			{/* «В избранное» в макете — кнопка 122×30 без заливки (r10): сердце 30 и
       подпись 14/18 w600 через 6. Такой высоты у DS-кнопки нет, поэтому своя. */}
			<button type="button" aria-pressed={saved} onClick={toggle} className="flex h-[30px] items-center gap-1.5 rounded-[10px] ds-h5 text-ds-primary outline-none transition-colors hover:text-ds-ink-2 focus-visible:ring-2 focus-visible:ring-ds-focus focus-visible:ring-offset-2">
				<FavoriteHeartIcon variant="page" active={saved} />
				{t("feed.saveToFavorites", {
        defaultValue: "В избранное"
      })}
			</button>
			<Button variant="ghost" size="icon-md" onClick={download} disabled={downloading} aria-busy={downloading} aria-label={t("feed.download", {
      defaultValue: "Скачать"
    })} title={t("feed.download", {
      defaultValue: "Скачать"
    })} className={ACTION}>
				<DownloadIcon />
			</Button>
			<Button variant="ghost" size="icon-md" onClick={share} aria-label={t("feed.share", {
      defaultValue: "Поделиться"
    })} title={t("feed.share", {
      defaultValue: "Поделиться"
    })} className={ACTION}>
				<ShareIcon />
			</Button>
			<Button variant="ghost" size="icon-md" aria-pressed={hidden} onClick={toggleHidden} aria-label={hidden ? t("feed.unhide", {
      defaultValue: "Вернуть идею"
    }) : t("feed.hide", {
      defaultValue: "Скрыть идею"
    })} title={hidden ? t("feed.unhide", {
      defaultValue: "Вернуть идею"
    }) : t("feed.hide", {
      defaultValue: "Скрыть идею"
    })} className={cn(ACTION, hidden && "text-ds-accent")}>
				<EyeOffIcon />
			</Button>
		</div>;
}